import { apiClient } from "./apiClient";
import { MediaShareResponse } from "./mediaShareService";

export interface Collection {
  id: string;
  clientId: string;
  name: string | null;
  briefId: string | null;
  createdBy: string;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CollectionWithItems extends Collection {
  items: CollectionMediaItem[];
}

// CollectionMediaItem now matches the full MediaItemData shape
// from the enriched collection items endpoint
export type CollectionMediaItem = import("@/core/MediaItem/MediaItem.model").MediaItemData;

export interface MediaTypeCounts {
  photo?: number;
  video?: number;
  audio?: number;
}

export interface CreateCollectionRequest {
  name?: string;
  briefId?: string;
  ideaId?: string; // Translated to entity link on the backend
}

export interface UpdateCollectionRequest {
  name?: string | null;
  briefId?: string | null;
}

export interface AddItemsResult {
  addedCount: number;
  requestedCount: number;
  collectionId: string;
}

export interface RemoveItemsResult {
  removedCount: number;
  requestedCount: number;
  collectionId: string;
}

export interface CollectionItemsResponse {
  items: CollectionMediaItem[];
  total: number;
  offset: number;
  limit: number;
}

/**
 * Get all collections for a client
 */
export const getCollections = async (
  clientId: string
): Promise<Collection[]> => {
  const response = await apiClient.get<{ collections: Collection[] }>(
    `/clients/${clientId}/collections`
  );
  return response.collections;
};

/**
 * Get a single collection by ID
 */
export const getCollection = async (
  clientId: string,
  collectionId: string,
  includeItems = false
): Promise<Collection | CollectionWithItems> => {
  const url = `/clients/${clientId}/collections/${collectionId}${includeItems ? "?includeItems=true" : ""}`;
  return apiClient.get<Collection | CollectionWithItems>(url);
};

/**
 * Create a new collection
 */
export const createCollection = async (
  clientId: string,
  data: CreateCollectionRequest
): Promise<Collection> => {
  return apiClient.post<Collection>(`/clients/${clientId}/collections`, data);
};

/**
 * Update a collection
 */
export const updateCollection = async (
  clientId: string,
  collectionId: string,
  data: UpdateCollectionRequest
): Promise<Collection> => {
  return apiClient.patch<Collection>(
    `/clients/${clientId}/collections/${collectionId}`,
    data
  );
};

/**
 * Delete a collection
 */
export const deleteCollection = async (
  clientId: string,
  collectionId: string
): Promise<void> => {
  await apiClient.delete(`/clients/${clientId}/collections/${collectionId}`);
};

/**
 * Get items in a collection with pagination and optional filters
 */
export const getCollectionItems = async (
  clientId: string,
  collectionId: string,
  options: {
    offset?: number;
    limit?: number;
    contentType?: string;
    mediaType?: string;
  } = {}
): Promise<CollectionItemsResponse> => {
  const { offset = 0, limit = 50, contentType, mediaType } = options;
  const params = new URLSearchParams();
  params.set("offset", String(offset));
  params.set("limit", String(limit));
  if (contentType) params.set("content_type", contentType);
  if (mediaType) params.set("media_type", mediaType);
  return apiClient.get<CollectionItemsResponse>(
    `/clients/${clientId}/collections/${collectionId}/items?${params.toString()}`
  );
};

/**
 * Get media type counts for a collection (for default tab selection)
 */
export const getCollectionMediaTypeCounts = async (
  clientId: string,
  collectionId: string
): Promise<MediaTypeCounts> => {
  return apiClient.get<MediaTypeCounts>(
    `/clients/${clientId}/collections/${collectionId}/media-type-counts`
  );
};

/**
 * Get all item IDs in a collection (for syncing selection state)
 */
export const getCollectionItemIds = async (
  clientId: string,
  collectionId: string
): Promise<number[]> => {
  const response = await apiClient.get<{ itemIds: number[] }>(
    `/clients/${clientId}/collections/${collectionId}/item-ids`
  );
  return response.itemIds;
};

/**
 * Add media items to a collection
 */
export const addItemsToCollection = async (
  clientId: string,
  collectionId: string,
  mediaItemIds: number[]
): Promise<AddItemsResult> => {
  return apiClient.post<AddItemsResult>(
    `/clients/${clientId}/collections/${collectionId}/items`,
    { mediaItemIds }
  );
};

/**
 * Remove media items from a collection
 */
export const removeItemsFromCollection = async (
  clientId: string,
  collectionId: string,
  mediaItemIds: number[]
): Promise<RemoveItemsResult> => {
  return apiClient.delete<RemoveItemsResult>(
    `/clients/${clientId}/collections/${collectionId}/items`,
    { mediaItemIds }
  );
};

/**
 * Get the collection linked to a brief
 */
export const getCollectionByBrief = async (
  briefId: string
): Promise<Collection | null> => {
  const response = await apiClient.get<Collection | null>(
    `/briefs/${briefId}/collection`
  );
  return response;
};

/**
 * Get the collection linked to an idea
 */
export const getCollectionByIdea = async (
  ideaId: string
): Promise<Collection | null> => {
  const response = await apiClient.get<Collection | null>(
    `/ideas/${ideaId}/collection`
  );
  return response;
};

/**
 * Create a shareable download link for all media items in a collection
 */
export const createCollectionShare = async (
  clientId: string,
  collectionId: string,
  retentionDays: number = 7
): Promise<MediaShareResponse> => {
  return apiClient.post<MediaShareResponse>(
    `/clients/${clientId}/collections/${collectionId}/share`,
    { retentionDays }
  );
};
